import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import IdentifiersSection from '../components/identity/IdentifiersSection.jsx';
import PlantingsSection from './plantings/PlantingsSection.jsx';
import OwnListsSection from './plantings/OwnListsSection.jsx';
import LogoSection from '../components/identity/LogoSection.jsx';
import Alert from '../components/ui/Alert.jsx';
import ConfirmModal from '../components/ui/ConfirmModal.jsx';
import CopyRichButton from '../components/ui/CopyRichButton.jsx';
import CopyButton from '../components/ui/CopyButton.jsx';
import { HowThisPageWorks, Loading } from '../components/ui/index.jsx';
import { fmtDateRange } from '../utils/format.js';
import CheckPreviewModal from './events/CheckPreviewModal.jsx';
import EventModal from './events/EventModal.jsx';
import StampsSection from './events/StampsSection.jsx';

const DNS_TEXT = {
  none: { tone: 'muted', text: 'Set up automatically when the event goes live.' },
  pending: { tone: 'muted', text: 'Setting up the address… this takes a minute.' },
  active: { tone: 'good', text: 'Ready: emails forwarded here reach us and appear in your Inbox.' },
  failed: { tone: 'bad', text: 'We couldn’t set up this address. Try again; if it keeps failing, email support@summitshield.io.' },
  removing: { tone: 'muted', text: 'Removing the address…' },
  removed: { tone: 'muted', text: 'Address removed.' },
  not_configured: { tone: 'muted', text: 'This server doesn’t set up email addresses automatically. Mail still works here.' },
};

function MailDnsStatus({ dns, live, onRetry, busy }) {
  const info = DNS_TEXT[dns.status] || DNS_TEXT.none;
  return (
    <div className={`dns-status dns-${info.tone}`} aria-live="polite">
      <span>{info.text}</span>
      {dns.status === 'failed' && dns.error && <span className="dns-error" title={dns.error}>Technical detail for support: {dns.error}</span>}
      {dns.status === 'failed' && live && (
        <button type="button" className="btn btn-small btn-secondary" onClick={onRetry} disabled={busy}>{busy ? 'Retrying…' : 'Retry'}</button>
      )}
    </div>
  );
}

/** Tabs as a real tab list: arrow keys move between them, the URL keeps the one open. */
function Tabs({ tabs, current, onChange }) {
  const keys = tabs.map((t) => t.key);
  const move = (e) => {
    const i = keys.indexOf(current);
    const next = e.key === 'ArrowRight' ? keys[(i + 1) % keys.length] : e.key === 'ArrowLeft' ? keys[(i - 1 + keys.length) % keys.length] : null;
    if (next) { e.preventDefault(); onChange(next); document.getElementById(`tab-${next}`)?.focus(); }
  };
  return (
    <div className="msg-views ev-tabs" role="tablist" aria-label="Event sections" onKeyDown={move}>
      {tabs.map((t) => (
        <button key={t.key} id={`tab-${t.key}`} type="button" role="tab" aria-selected={current === t.key} aria-controls={`panel-${t.key}`}
          tabIndex={current === t.key ? 0 : -1} className={`msg-view${current === t.key ? ' is-on' : ''}`} onClick={() => onChange(t.key)}>
          {t.label}{t.count != null && ` (${t.count})`}
        </button>
      ))}
    </div>
  );
}

export default function EventDetail() {
  const { eventId } = useParams();
  const [params, setParams] = useSearchParams();
  const { organisation } = useAuth();
  const [event, setEvent] = useState(null);
  const [own, setOwn] = useState([]);
  const [inherited, setInherited] = useState([]);
  const [testMode, setTestMode] = useState(false);
  const [brands, setBrands] = useState([]);
  const [error, setError] = useState('');
  const [retryBusy, setRetryBusy] = useState(false);
  const [modal, setModal] = useState(null);   // 'edit' | 'live' | 'offline' | 'check'

  const load = useCallback(async () => {
    try {
      const t = await api(`/portal/events/${eventId}/trust`);
      setEvent(t.event); setOwn(t.identifiers); setInherited(t.inherited); setTestMode(!!t.test_mode); setError('');
    } catch (err) {
      setError(err.status === 404 ? 'This event doesn’t exist.' : err.message);
    }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!organisation) return;
    api(`/portal/organisations/${organisation.id}/brands`).then((d) => setBrands(d.brands.filter((b) => b.status === 'active'))).catch(() => {});
  }, [organisation]);

  // While the mail address is being set up, check back every few seconds
  const dnsStatus = event?.mail_dns?.status;
  useEffect(() => {
    if (!['pending', 'removing'].includes(dnsStatus)) return undefined;
    const t = setInterval(() => { api(`/portal/events/${eventId}`).then((e) => setEvent(e.event)).catch(() => {}); }, 3000);
    return () => clearInterval(t);
  }, [dnsStatus, eventId]);

  const back = <Link to="/events" className="back-link">← Your events</Link>;
  if (error) return <div className="page"><div className="page-header-top">{back}</div><Alert tone="danger">{error}</Alert></div>;
  if (!event) return <div className="page"><Loading /></div>;

  const live = event.status === 'live';
  const trusted = own.length + inherited.length;
  const manager = ['owner', 'admin'].includes(organisation.role);
  const TABS = [
    { key: 'share', label: 'Share it' },
    { key: 'channels', label: 'Official channels', count: trusted },
    { key: 'logo', label: 'Logo' },
    manager && { key: 'own_lists', label: 'Watch your emails' },
    manager && { key: 'planted', label: 'Planted contacts' },
  ].filter(Boolean);
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'share';
  const setTab = (k) => setParams(k === 'share' ? {} : { tab: k }, { replace: true });

  const retryDns = async () => {
    setRetryBusy(true);
    try { const { event: updated } = await api(`/portal/events/${eventId}/mail-dns/retry`, { method: 'POST' }); setEvent(updated); }
    catch (err) { setError(err.message); }
    finally { setRetryBusy(false); }
  };
  const setStatus = async (status) => {
    const { event: updated } = await api(`/portal/events/${eventId}`, { method: 'PATCH', body: { status } });
    setEvent(updated);
    setModal(null);
  };
  // What's left before people can use it, as ticks
  const ready = [
    { done: trusted > 0, title: 'Add its official channels', text: 'The email addresses, websites, phone numbers and bank details that are really yours. Your company’s already count.', action: <Link to="?tab=channels" className="btn btn-small btn-secondary">Add channels</Link> },
    { done: !testMode, title: 'Prove your domain is yours', text: 'Until then the check page isn’t public and the forwarding address only takes mail from your own domain.', action: <Link to="/company" className="btn btn-small btn-secondary">Prove it</Link> },
    { done: live, title: 'Go live', text: 'The check page starts answering and the forwarding address starts taking mail.', action: trusted > 0 ? <button type="button" className="btn btn-small btn-primary" onClick={() => setModal('live')}>Go live</button> : null },
  ];
  const state = live && !testMode ? 'live' : live ? 'test' : 'draft';
  const STATE = {
    live: ['Live', 'good', 'People can check emails, calls and websites against this event, and forward suspicious emails to us.'],
    test: ['Live, in test mode', 'warn', 'It works, but only for you: the check page isn’t public until you prove your domain is yours.'],
    draft: [event.status === 'archived' ? 'Archived' : 'Not live yet', 'neutral', 'Nobody can check anything against it yet.'],
  }[state];

  return (
    <div className="page">
      <div className="page-header-top">{back}<HowThisPageWorks topic="events" /></div>

      <section className="card ev-hero" aria-labelledby="ev-h">
        <div className="ld-tags">
          <span className={`msg-pill tone-${STATE[1]}`}>{STATE[0]}</span>
          <span className="muted">{fmtDateRange(event.starts_on, event.ends_on)}{event.brand && <> · <Link to={`/company/brands/${event.brand.id}`}>{event.brand.name}</Link></>}</span>
        </div>
        <div className="ev-hero-main">
          <div className="ev-hero-text">
            <h1 id="ev-h">{event.name}</h1>
            <p className="ev-hero-state">{STATE[2]}</p>
          </div>
          <div className="ev-hero-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setModal('check')}>Try a check</button>
            <button type="button" className="btn btn-secondary" onClick={() => setModal('edit')}>Edit details</button>
            {live
              ? <button type="button" className="btn btn-secondary" onClick={() => setModal('offline')}>Take offline</button>
              : <button type="button" className="btn btn-primary" disabled={trusted === 0} title={trusted === 0 ? 'Add an official channel first' : undefined} onClick={() => setModal('live')}>Go live</button>}
          </div>
        </div>

        {ready.some((r) => !r.done) && (
          <div className="ev-ready">
            <h2>Getting it ready</h2>
            <ul>
              {ready.map((r) => (
                <li key={r.title} className={r.done ? 'is-done' : ''}>
                  {r.done
                    ? <span className="plan-tick" aria-label="Done"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M8.5 12l2.5 2.5 4.5-5" /></svg></span>
                    : <span className="plan-circle" aria-label="Not done yet" />}
                  <div className="plan-step-text"><strong>{r.title}</strong>{!r.done && <span className="muted">{r.text}</span>}</div>
                  {!r.done && r.action}
                </li>
              ))}
            </ul>
            <p className="muted small">Use <strong>Try a check</strong> to see exactly what people will be told.</p>
          </div>
        )}
      </section>

      <Tabs tabs={TABS} current={tab} onChange={setTab} />

      <div role="tabpanel" id={`panel-${tab}`} aria-labelledby={`tab-${tab}`} className="tab-panel">
      {tab === 'share' && (
        <div className="ev-share">
          <section className="card home-checkpage" aria-labelledby="ev-check-h">
            <div className="home-checkpage-text">
              <h2 id="ev-check-h">Your check page</h2>
              <p>Exhibitors and attendees paste an email, describe a call or ask a question here, and get an answer straight away.</p>
            </div>
            <div className="home-checkpage-link">
              <p className="home-checkpage-url">{event.verify_url.replace(/^https?:\/\//, '')}</p>
              <div className="home-checkpage-actions">
                <CopyButton text={event.verify_url} label="Copy link" />
                <a href={event.verify_url} target="_blank" rel="noreferrer" className="btn btn-small btn-on-dark">Open it</a>
              </div>
            </div>
          </section>

          <div className="ev-share-grid">
            <section className="card" aria-labelledby="ev-fwd-h">
              <h2 id="ev-fwd-h">Forwarding address</h2>
              <p className="muted">Anyone can forward a suspicious email here. We check it and reply to them, and anything we can’t settle comes to your Messages.</p>
              <div className="ev-copy-row"><span className="mono">{event.check_address}</span><CopyButton text={event.check_address} /></div>
              <MailDnsStatus dns={event.mail_dns} live={live} onRetry={retryDns} busy={retryBusy} />
            </section>

            <section className="card" aria-labelledby="ev-badge-h">
              <h2 id="ev-badge-h">Badge for your emails</h2>
              <p className="muted">Put it at the bottom of your emails so people can check them in one click.</p>
              <a href={event.verify_url} target="_blank" rel="noopener noreferrer" title="Opens your check page">
                <img className="badge-preview" src={event.badge_png_url} alt="Verify this email - SummitShield" width="236" height="32" />
              </a>
              <div className="ev-copy-row">
                <CopyRichButton primary label="Copy for your signature"
                  html={`<a href="${event.verify_url}" target="_blank"><img src="${event.badge_png_url}" alt="Verify this email - SummitShield" width="236" height="32" style="border:0;display:block"></a>`}
                  text={`Verify this email: ${event.verify_url}`} />
                <CopyButton text={`<a href="${event.verify_url}"><img src="${event.badge_png_url}" alt="Verify this email - SummitShield" width="236" height="32" style="border:0"></a>`} label="Copy badge code" />
              </div>
              <ul className="plain-list small badge-steps">
                <li><strong>Outlook, Gmail or Apple Mail:</strong> press <em>Copy for your signature</em>, open your signature settings and paste. The picture arrives with its link already set.</li>
                <li><strong>Newsletter tools</strong> (Mailchimp, HubSpot and so on): add an HTML block and paste the badge code.</li>
              </ul>
              <p className="muted small">This badge opens your check page, and anyone can copy it. For an important email, use a stamp instead (below): it belongs to that one email.</p>
            </section>
          </div>

          <StampsSection event={event} />

          <section className="card" aria-labelledby="ev-how-h">
            <h2 id="ev-how-h">What people are told when they check</h2>
            <div className="ev-answers">
              <div className="ev-answer tone-good"><strong>It’s official</strong><span>It matches one of this event’s channels, or one it shares with {event.brand ? 'its brand or ' : ''}your company.</span></div>
              <div className="ev-answer tone-warn"><strong>It isn’t theirs</strong><span>Anything else: lookalike domains, free email accounts, other bank accounts.</span></div>
              <div className="ev-answer tone-bad"><strong>It’s a reported scam</strong><span>Not yours, and already reported as a scam here or at another event.</span></div>
            </div>
            <p className="muted small">We never tell anyone something is “safe”, only whether it matches what you’ve listed. <Link to="?tab=channels">See what counts as official ({trusted})</Link></p>
          </section>
        </div>
      )}

      {tab === 'logo' && <LogoSection base={`/portal/organisations/${event.organisation_id}`} entityId={event.entity_id} ownerName={event.name} canEdit title="Event logo"
        description={`Shown at the top of ${event.name}’s public verify page. Leave it empty to use ${event.brand ? 'the brand’s or ' : ''}your company’s logo.`} />}

      {tab === 'channels' && <IdentifiersSection
        title="This event’s own channels"
        description="Only for this event: its microsite, official hotel booking links, an event hotline. Your company’s channels already count — no need to repeat them."
        basePath={`/portal/organisations/${event.organisation_id}/entities/${event.entity_id}/identifiers`}
        ownerName={event.name}
        initial={own}
        onChange={setOwn}
        inherited={inherited}
        inheritedTitle="Also trusted for this event"
        inheritedDescription={manager
          ? `From ${event.brand ? `${event.brand.name}, ` : ''}your company, its people and suppliers. Edit changes who can see one (and its label) everywhere it counts; add or remove them on the ${event.brand ? 'brand or ' : ''}company page.`
          : `From ${event.brand ? `${event.brand.name} and ` : ''}your company. An owner or admin can change them.`}
        organisationId={event.organisation_id}
        canEditInherited={manager}
      />}

      {/* Owners and admins only: a planted address that leaks is a burned trap */}
      {tab === 'own_lists' && manager && <OwnListsSection organisationId={event.organisation_id} eventId={event.id} />}
      {tab === 'planted' && manager && <PlantingsSection organisationId={event.organisation_id} eventId={event.id} />}
      </div>

      {modal === 'check' && <CheckPreviewModal event={event} onClose={() => setModal(null)} />}
      {modal === 'edit' && (
        <EventModal event={event} brands={brands} onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }} />
      )}
      {modal === 'live' && (
        <ConfirmModal title="Go live?" confirmLabel="Go live" onClose={() => setModal(null)} onConfirm={() => setStatus('live')}>
          {testMode
            ? <p>Your company is in test mode, so the verify page stays private and the forwarding address only accepts mail from your own domain until a domain is verified. {trusted} channel{trusted === 1 ? '' : 's'} will count as yours.</p>
            : <p>The verify page at <span className="mono">{event.verify_url.replace(/^https?:\/\//, '')}</span> starts answering, and the forwarding address starts accepting mail. {trusted} channel{trusted === 1 ? '' : 's'} will count as yours.</p>}
        </ConfirmModal>
      )}
      {modal === 'offline' && (
        <ConfirmModal title="Take this event offline?" confirmLabel="Take offline" danger onClose={() => setModal(null)} onConfirm={() => setStatus('draft')}>
          <p>Its verify page will say there&rsquo;s no such event, and its forwarding address will refuse mail, until you go live again. Anyone checking a suspicious message in the meantime gets no answer.</p>
        </ConfirmModal>
      )}
    </div>
  );
}
